import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, Clock, AlertTriangle, Plus, Edit3, X, Loader2,
  ChevronRight, User, Calendar, FileText, Upload, Mic, Search,
  Filter, MoreVertical, Send, Eye, CheckSquare, Circle, Trash2
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const TASK_TYPES = [
  { id: 'approval', label: 'Approval Needed', icon: CheckCircle2, color: 'bg-green-500/20 text-green-400' },
  { id: 'upload', label: 'Upload Requested', icon: Upload, color: 'bg-blue-500/20 text-blue-400' },
  { id: 'availability', label: 'Availability Request', icon: Calendar, color: 'bg-purple-500/20 text-purple-400' },
  { id: 'recording', label: 'Recording Needed', icon: Mic, color: 'bg-orange-500/20 text-orange-400' },
  { id: 'decision', label: 'Decision Required', icon: AlertTriangle, color: 'bg-red-500/20 text-red-400' },
  { id: 'general', label: 'General Task', icon: FileText, color: 'bg-gray-500/20 text-gray-400' }
];

const PRIORITIES = [
  { id: 'high', label: 'High', color: 'bg-red-500/20 text-red-400' },
  { id: 'medium', label: 'Medium', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 'low', label: 'Low', color: 'bg-gray-500/20 text-gray-400' }
];

const STATUSES = {
  not_started: { label: 'Not Started', color: 'bg-gray-500/20 text-gray-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
  submitted: { label: 'Submitted', color: 'bg-green-500/20 text-green-400' },
  needs_clarification: { label: 'Needs Clarification', color: 'bg-orange-500/20 text-orange-400' },
  completed: { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400' }
};

const AEAdminTasks = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Create form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    instructions: '',
    task_type: 'general',
    priority: 'medium',
    due_date: '',
    assigned_to: 'akayleb@operatr.com',
    client_visible: true
  });

  useEffect(() => {
    if (userEmail) {
      fetchTasks();
    }
  }, [userEmail]);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return;
    
    setActionLoading('create');
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTask)
      });
      const data = await res.json();
      if (data.success) {
        setShowCreateModal(false);
        setNewTask({
          title: '',
          description: '',
          instructions: '',
          task_type: 'general',
          priority: 'medium',
          due_date: '',
          assigned_to: 'akayleb@operatr.com',
          client_visible: true
        });
        fetchTasks();
      }
    } catch (err) {
      console.error('Error creating task:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkComplete = async (taskId) => {
    setActionLoading('complete');
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks/${taskId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });
      const data = await res.json();
      if (data.success) {
        fetchTasks();
        setSelectedTask(prev => prev ? { ...prev, status: 'completed' } : null);
      }
    } catch (err) {
      console.error('Error completing task:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestClarification = async (taskId) => {
    setActionLoading('clarify');
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks/${taskId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'needs_clarification' })
      });
      const data = await res.json();
      if (data.success) {
        fetchTasks();
        setSelectedTask(prev => prev ? { ...prev, status: 'needs_clarification' } : null);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'pending') return ['not_started', 'in_progress'].includes(task.status);
    if (filter === 'submitted') return task.status === 'submitted';
    if (filter === 'completed') return task.status === 'completed';
    if (filter === 'overdue') {
      return task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
    }
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => ['not_started', 'in_progress'].includes(t.status)).length,
    submitted: tasks.filter(t => t.status === 'submitted').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-admin-tasks-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ELLE MAXWELL AGENCY</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-admin-tasks-title">
                Task Management
              </h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
              data-testid="ae-admin-create-task-btn"
            >
              <Plus className="w-4 h-4" />
              New Client Request
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-gray-400">Total Tasks</p>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold text-blue-400">{stats.pending}</p>
            <p className="text-xs text-gray-400">Pending</p>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold text-green-400">{stats.submitted}</p>
            <p className="text-xs text-gray-400">Submitted</p>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4">
            <p className="text-2xl font-bold text-emerald-400">{stats.completed}</p>
            <p className="text-xs text-gray-400">Completed</p>
          </div>
          <div className="bg-white/5 border border-red-500/20 rounded-xl p-4">
            <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
            <p className="text-xs text-gray-400">Overdue</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            {[
              { id: 'all', label: 'All Tasks' },
              { id: 'pending', label: 'Pending' },
              { id: 'submitted', label: 'Submitted' },
              { id: 'completed', label: 'Completed' },
              { id: 'overdue', label: 'Overdue' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filter === f.id 
                    ? 'bg-[#C4A052] text-black font-medium' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[#C4A052] w-64"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Task List */}
          <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-380px)] overflow-y-auto pr-2">
            {filteredTasks.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-8 text-center">
                <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No tasks found</p>
              </div>
            ) : (
              filteredTasks.map(task => {
                const taskType = TASK_TYPES.find(t => t.id === task.task_type) || TASK_TYPES[5];
                const TaskIcon = taskType.icon;
                const status = STATUSES[task.status] || STATUSES.not_started;
                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
                
                return (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                      selectedTask?.id === task.id ? 'border-[#C4A052]' : 'border-white/10'
                    } ${isOverdue ? 'border-red-500/30' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${taskType.color}`}>
                        <TaskIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{task.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                            {status.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        {task.due_date && (
                          <p className={`text-xs mt-2 ${isOverdue ? 'text-red-400' : 'text-gray-500'}`}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Task Detail */}
          <div className="lg:col-span-2">
            {selectedTask ? (
              <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          TASK_TYPES.find(t => t.id === selectedTask.task_type)?.color || 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {TASK_TYPES.find(t => t.id === selectedTask.task_type)?.label || 'Task'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUSES[selectedTask.status]?.color}`}>
                          {STATUSES[selectedTask.status]?.label}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold">{selectedTask.title}</h2>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>Assigned to Akayleb</span>
                    </div>
                    {selectedTask.due_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Due {new Date(selectedTask.due_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</h3>
                    <p className="text-gray-300">{selectedTask.description || 'No description provided.'}</p>
                  </div>

                  {/* Instructions */}
                  {selectedTask.instructions && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Instructions</h3>
                      <div className="bg-white/5 rounded-lg p-4 text-sm text-gray-300">
                        {selectedTask.instructions}
                      </div>
                    </div>
                  )}

                  {/* Client Response */}
                  {selectedTask.response && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Client Response</h3>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <p className="text-sm text-gray-300">{selectedTask.response}</p>
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {selectedTask.files && selectedTask.files.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Uploaded Files</h3>
                      <div className="space-y-2">
                        {selectedTask.files.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{file}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Admin Actions */}
                  {selectedTask.status === 'submitted' && (
                    <div className="border-t border-white/10 pt-6">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Review Response</h3>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => handleMarkComplete(selectedTask.id)}
                          disabled={actionLoading === 'complete'}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === 'complete' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                          Mark Complete
                        </button>
                        <button
                          onClick={() => handleRequestClarification(selectedTask.id)}
                          disabled={actionLoading === 'clarify'}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === 'clarify' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <AlertTriangle className="w-4 h-4" />
                          )}
                          Request Clarification
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Status Messages */}
                  {selectedTask.status === 'completed' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <p className="text-emerald-400">This task has been completed</p>
                    </div>
                  )}

                  {selectedTask.status === 'needs_clarification' && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <p className="text-orange-400">Awaiting clarification from Akayleb</p>
                    </div>
                  )}

                  {['not_started', 'in_progress'].includes(selectedTask.status) && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <p className="text-blue-400">Waiting for Akayleb&apos;s response</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Select a Task</h3>
                <p className="text-gray-500 text-sm">Click on a task to view details and manage responses</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">New Client Request</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="What do you need from Akayleb?"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#C4A052]"
                  placeholder="Provide context..."
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Instructions</label>
                <textarea
                  value={newTask.instructions}
                  onChange={(e) => setNewTask(prev => ({ ...prev, instructions: e.target.value }))}
                  rows={3}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#C4A052]"
                  placeholder="Step-by-step instructions..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Request Type</label>
                  <select
                    value={newTask.task_type}
                    onChange={(e) => setNewTask(prev => ({ ...prev, task_type: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {TASK_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {PRIORITIES.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                disabled={!newTask.title.trim() || actionLoading === 'create'}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50"
              >
                {actionLoading === 'create' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send to Akayleb
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEAdminTasks;
